import { useCallback, useEffect, useState } from 'react';

// Endepunkt mot PHP-mellomlag
const API_URL = '/api/min-side/bruker_akt.php';

export function useDeltakerData() {
    const [data, setData] = useState({
        aktiviteter: [],
        lunsj_dager: [],
        middag_dager: [],
        maltider_allergier: '',
        aktivitet_pameld_apent: 'J'
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'hent' })
            });

            const json = await res.json();

            if (!json.success) {
                throw new Error(json.message || 'Kunne ikke hente data');
            }

            setData({
                aktiviteter: json.program ?? [],
                lunsj_dager: json.lunsj_dager ?? [],
                middag_dager: json.middag_dager ?? [],
                maltider_allergier: json.maltider_allergier ?? '',
                aktivitet_pameld_apent: json.aktivitet_pameld_apent ?? 'J'
            });
        } catch (err) {
            setError(err.message || 'Kunne ikke hente data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function hentData() {
            setLoading(true);
            setError(null);

            try {
                const res = await fetch(API_URL, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'hent' })
                });

                const json = await res.json();

                if (!json.success) {
                    throw new Error(json.message || 'Kunne ikke hente data');
                }

                if (!cancelled) {
                    setData({
                        aktiviteter: json.program ?? [],
                        lunsj_dager: json.lunsj_dager ?? [],
                        middag_dager: json.middag_dager ?? [],
                        maltider_allergier: json.maltider_allergier ?? '',
                        aktivitet_pameld_apent: json.aktivitet_pameld_apent ?? 'J'
                    });
                }
            } catch (err) {
                if (!cancelled) setError(err.message || 'Kunne ikke hente data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        hentData();

        return () => {
            cancelled = true;
        };
    }, []);

    const saveAktiviteterForDato = useCallback(async (dato, aktIds) => {
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'oppdater_aktiviteter',
                    akt_dato: dato,
                    aktiviteter: aktIds
                })
            });

            const json = await res.json();

            if (!json.success) {
                return {
                    success: false,
                    message: json.message || 'Kunne ikke lagre aktiviteter.'
                };
            }

            setData(prev => ({
                ...prev,
                aktiviteter: prev.aktiviteter.map(a =>
                    a.dato === dato
                        ? {
                              ...a,
                              pameldt: aktIds.includes(a.akt_id) ? 'J' : 'N'
                          }
                        : a
                )
            }));

            return {
                success: true,
                message: json.message || 'Aktivitetene er lagret.'
            };
        } catch (err) {
            return {
                success: false,
                message: err.message || 'Nettverksfeil ved lagring av aktiviteter.'
            };
        }
    }, []);

    const saveMaltider = useCallback(async ({ lunsjer, middager, allergier = '' }) => {
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'oppdater_maltider',
                    lunsjer,
                    middager,
                    allergier
                })
            });

            const json = await res.json();

            if (!json.success) {
                return {
                    success: false,
                    message: json.message || 'Kunne ikke lagre måltider.'
                };
            }

            setData(prev => ({
                ...prev,
                lunsj_dager: prev.lunsj_dager.map(d => ({
                    ...d,
                    pameldt: lunsjer.includes(d.dato) ? 'J' : 'N'
                })),
                middag_dager: prev.middag_dager.map(d => ({
                    ...d,
                    pameldt: middager.includes(d.dato) ? 'J' : 'N'
                })),
                maltider_allergier: allergier
            }));

            return {
                success: true,
                message: json.message || 'Måltidene er lagret.'
            };
        } catch (err) {
            return {
                success: false,
                message: err.message || 'Nettverksfeil ved lagring av måltider.'
            };
        }
    }, []);

    return {
        data,
        loading,
        error,
        refresh,
        saveAktiviteterForDato,
        saveMaltider
    };
}
